<script setup>
import '@campact/camper/core/CmprButton';
import '@campact/camper/core/CmprFormHint';
import '@campact/camper/cta/CmprPrefilledArea';
import '@campact/camper/icons/CmprIconSolidCheck';

import { computed, inject } from 'vue';
import logoutAndRedirect from '@cta/frontend/javascripts/logout';
import StepItem from './StepItem.vue';
import CmprInput from '@cta/frontend/components/CmprInput.vue';
import CmprSelect from '@cta/frontend/components/CmprSelect.vue';
import EmailAddress from './EmailAddress.vue';

const store = inject('store');

const props = defineProps({
  stepNumber: {
    type: Number,
    default: 2,
  },
  salutations: {
    type: Array,
    default() {
      return [];
    },
  },
  countryCodes: {
    type: Object,
    default() {
      return {};
    },
  },
  regions: {
    type: Array,
    default() {
      return [];
    },
  },
  prefilledAttributes: {
    type: Object,
    default() {
      return {
        salutation: undefined,
        firstname: undefined,
        lastname: undefined,
        countrycode: undefined,
        postalcode: undefined,
        region: undefined,
        locality: undefined,
        street: undefined,
        emailaddress: undefined,
      };
    },
  },
  collectAddress: {
    type: Boolean,
    default: true,
  },
  logoutPath: {
    type: String,
    default: undefined,
  },
  analyticsIdPrefix: {
    type: String,
    default: 'donate-call',
  },
});

const emit = defineEmits(['try-open', 'validate', 'commit']);

const attributes = [
  'salutation', 'firstname', 'lastname', 'countrycode', 'postalcode', 'region', 'locality', 'street', 'emailaddress',
];

const inputsEmpty = computed(() => [store.values.firstname, store.values.lastname,
  store.values.street, store.values.locality, store.values.postalcode,
  store.values.region, store.values.emailaddress].every((value) => !value));

const title = computed(() => {
  if (inputsEmpty.value) return 'Adressdaten eintragen';
  if (store.addressStepValid) return 'Adressdaten vollständig';
  return 'Adressdaten vervollständigen';
});
const summaryTitle = computed(() => [
  props.collectAddress && store.values.salutation !== 'Hallo' ? store.values.salutation : undefined,
  store.values.firstname,
  store.values.lastname,
].filter((value) => !!value).join(' '));
const summaryBody = computed(() => [
  props.collectAddress ? store.values.street : undefined, props.collectAddress ? store.values.countrycode : undefined,
  [
    props.collectAddress ? (store.values.countrycode == 'RO' ? store.values.region : store.values.postalcode) : undefined,
    props.collectAddress ? store.values.locality : undefined,
  ].filter((value) => !!value).join(' '),
  store.values.emailaddress,
].filter((value) => !!value).join(' · '));

const anythingPrefilled = computed(
  () => Object.keys(props.prefilledAttributes).filter((attribute) => attributes.includes(attribute))
    .some((attribute) => props.prefilledAttributes[attribute]),
);

function showErrorMessage(attribute) {
  return store.addressErrorUnlocked && store.errors[attribute];
}

function validate(attribute) {
  if (showErrorMessage(attribute)) emit('validate');
}
</script>

<template>
  <div class="address-step">
    <StepItem
      :number="stepNumber"
      :opened="store.addressStepOpen"
      :title="title"
      tls-notice="Die Übermittlung Deiner Daten erfolgt sicher via SSL-Verschlüsselung."
      @try-open="emit('try-open')"
    >
      <template #opened>
        <div class="address-step__body">
          <a
            v-if="anythingPrefilled"
            class="address-step__logout-link"
            @click="logoutAndRedirect(logoutPath)"
          >
            Das bin ich nicht
          </a>

          <div class="address-step-input-grid">
            <div
              v-if="collectAddress"
              class="address-step-input-grid__column-one"
            >
              <CmprSelect
                :id="`${analyticsIdPrefix}-form-contact-details-salutation-dropdown`"
                v-model="store.values.salutation"
                aria-label="Anrede"
                autocomplete="honorific-prefix"
                class="address-step__salutation"
                data-analytics-context="address-step"
                :data-analytics-id="`${analyticsIdPrefix}-form-contact-details-salutation-dropdown`"
                data-analytics-inner-text="salutation"
              >
                <option
                  v-for="element in salutations"
                  :key="element"
                  :value="element"
                >
                  {{ element }}
                </option>
              </CmprSelect>
            </div>

            <div
              class="address-step-input-grid__column-one"
            >
              <CmprInput
                :id="`${analyticsIdPrefix}-form-contact-details-firstname-field`"
                v-model="store.values.firstname"
                autocomplete="given-name"
                class="address-step__firstname"
                :class="{'is-invalid': showErrorMessage('firstname')}"
                data-analytics-context="address-step"
                :data-analytics-id="`${analyticsIdPrefix}-form-contact-details-firstname-field`"
                data-analytics-inner-text="firstname"
                placeholder="Vorname"
                @input="validate('firstname')"
                @keydown.enter="emit('commit')"
              />

              <cmpr-form-hint
                v-if="showErrorMessage('firstname')"
                variant="error"
              >
                {{ store.errors.firstname }}
              </cmpr-form-hint>
            </div>

            <div
              class="address-step-input-grid__column-two"
            >
              <CmprInput
                :id="`${analyticsIdPrefix}-form-contact-details-lastname-field`"
                v-model="store.values.lastname"
                autocomplete="family-name"
                class="address-step__lastname"
                :class="{'is-invalid': showErrorMessage('lastname')}"
                data-analytics-context="address-step"
                :data-analytics-id="`${analyticsIdPrefix}-form-contact-details-lastname-field`"
                data-analytics-inner-text="lastname"
                placeholder="Nachname"
                @input="validate('lastname')"
                @keydown.enter="emit('commit')"
              />

              <cmpr-form-hint
                v-if="showErrorMessage('lastname')"
                variant="error"
              >
                {{ store.errors.lastname }}
              </cmpr-form-hint>
            </div>

            <div
              v-if="collectAddress"
              class="address-step-input-grid__column-span"
            >
              <CmprInput
                :id="`${analyticsIdPrefix}-form-contact-details-street-field`"
                v-model="store.values.street"
                autocomplete="street-address"
                class="address-step__street"
                :class="{'is-invalid': showErrorMessage('street')}"
                data-analytics-context="address-step"
                :data-analytics-id="`${analyticsIdPrefix}-form-contact-details-street-field`"
                data-analytics-inner-text="street"
                placeholder="Straße und Hausnummer"
                @input="validate('street')"
                @keydown.enter="emit('commit')"
              />

              <cmpr-form-hint
                v-if="showErrorMessage('street')"
                variant="error"
              >
                {{ store.errors.street }}
              </cmpr-form-hint>
            </div>

            <div
              v-if="collectAddress"
              class="address-step-input-grid__column-one"
            >
              <CmprSelect
                :id="`${analyticsIdPrefix}-form-contact-details-countrycode-dropdown`"
                v-model="store.values.countrycode"
                aria-label="Land"
                autocomplete="country"
                class="address-step__countrycode"
                data-analytics-context="address-step"
                :data-analytics-id="`${analyticsIdPrefix}-form-contact-details-countrycode-dropdown`"
                data-analytics-inner-text="countrycode"
              >
                <option
                  v-for="(code, name) in countryCodes"
                  :key="code"
                  :value="code"
                >
                  {{ name }}
                </option>
              </CmprSelect>
            </div>

            <div
              v-if="collectAddress && store.values.countrycode != 'RO'"
              class="address-step-input-grid__column-two"
            >
              <CmprInput
                :id="`${analyticsIdPrefix}-form-contact-details-postalcode-field`"
                v-model="store.values.postalcode"
                autocomplete="postal-code"
                class="address-step__postalcode"
                :class="{'is-invalid': showErrorMessage('postalcode')}"
                data-analytics-context="address-step"
                :data-analytics-id="`${analyticsIdPrefix}-form-contact-details-postalcode-field`"
                data-analytics-inner-text="postalcode"
                inputmode="numeric"
                placeholder="PLZ"
                @input="validate('postalcode')"
                @keydown.enter="emit('commit')"
              />

              <cmpr-form-hint
                v-if="showErrorMessage('postalcode')"
                variant="error"
              >
                {{ store.errors.postalcode }}
              </cmpr-form-hint>
            </div>

            <div
              v-if="collectAddress && store.values.countrycode == 'RO'"
              class="address-step-input-grid__column-two"
            >
              <CmprSelect
                :id="`${analyticsIdPrefix}-form-contact-details-region-field`"
                v-model="store.values.region"
                aria-label="Kreis"
                autocomplete="address-level1"
                class="address-step__region"
                :class="{'is-invalid': showErrorMessage('region')}"
                data-analytics-context="address-step"
                :data-analytics-id="`${analyticsIdPrefix}-form-contact-details-region-field`"
                data-analytics-inner-text="region"
                @change="validate('region')"
              >
                <option
                  disabled="disabled"
                  :value="undefined"
                >
                  Bitte wählen
                </option>
                <option
                  v-for="(name) in regions"
                  :key="name"
                  :value="name"
                >
                  {{ name }}
                </option>
              </CmprSelect>

              <cmpr-form-hint
                v-if="showErrorMessage('region')"
                variant="error"
              >
                {{ store.errors.region }}
              </cmpr-form-hint>
            </div>

            <div
              v-if="collectAddress"
              class="address-step-input-grid__column-span"
            >
              <CmprInput
                :id="`${analyticsIdPrefix}-form-contact-details-locality-field`"
                v-model="store.values.locality"
                autocomplete="address-level2"
                class="address-step__locality"
                :class="{'is-invalid': showErrorMessage('locality')}"
                data-analytics-context="address-step"
                :data-analytics-id="`${analyticsIdPrefix}-form-contact-details-locality-field`"
                data-analytics-inner-text="locality"
                placeholder="Ort"
                @input="validate('locality')"
                @keydown.enter="emit('commit')"
              />

              <cmpr-form-hint
                v-if="showErrorMessage('locality')"
                variant="error"
              >
                {{ store.errors.locality }}
              </cmpr-form-hint>
            </div>

            <div class="address-step-input-grid__column-span">
              <slot name="emailaddress">
                <EmailAddress
                  v-model="store.values.emailaddress"
                  analytics-context="address-step"
                  :analytics-id-prefix="analyticsIdPrefix"
                  :disabled="!!prefilledAttributes.emailaddress"
                  :error="showErrorMessage('emailaddress') ? String(store.errors.emailaddress) : undefined"
                  input-class="address-step__emailaddress"
                  @commit="emit('commit')"
                  @input="validate('emailaddress')"
                />
              </slot>
            </div>
          </div>

          <cmpr-button
            class="w-100"
            data-analytics-context="address-step"
            :data-analytics-id="`${analyticsIdPrefix}-form-contact-details-commit-button`"
            size="small"
            variant="secondary"
            @click.prevent="emit('commit')"
          >
            Weiter
          </cmpr-button>
        </div>
      </template>

      <template #closed>
        <cmpr-prefilled-area
          v-if="!inputsEmpty"
          class="address-step__summary"
          editable="editable"
          :variant="store.addressStepValid ? 'success' : 'warning'"
        >
          <div
            class="fw-bold"
          >
            {{ summaryTitle }}
          </div>

          <span
            v-if="summaryBody"
          >
            {{ summaryBody }}
          </span>
        </cmpr-prefilled-area>
      </template>
    </StepItem>
  </div>
</template>

<style scoped lang="scss">
.address-step__body {
  display: flex;
  flex-direction: column;
  gap: var(--cmpr-space-60);
}

.address-step__logout-link {
  align-self: start;
  cursor: pointer;
  font-size: var(--cmpr-font-size-30);
  font-weight: var(--cmpr-font-weight-20);
  line-height: var(--cmpr-font-line-height-30);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.address-step-input-grid {
  display: grid;
  gap: var(--cmpr-space-60);
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-rows: min-content;

  &:empty {
    display: none;
  }
}

.address-step-input-grid__column-one {
  grid-column: 1;
}

.address-step-input-grid__column-two {
  grid-column: 2;
}

.address-step-input-grid__column-span {
  grid-column: span 2;
}
</style>
